/*
  <card-message-field> — required, length-capped line-item message for products
  tagged `personalized-message`. Rendered by snippets/card-message-field.liquid;
  validated by assets/product-form.js before the add-to-cart fetch (the product
  form is `novalidate`, so the browser never enforces `required` itself).

  The textarea's `maxlength` is the length cap, so validate() only enforces
  non-emptiness after whitespace normalization.

  The define is guarded because buy-buttons.liquid can render twice on one page
  (a featured-product section beside the main PDP), which re-emits the asset tag.
*/
if (!customElements.get('card-message-field')) {
  class CardMessageField extends HTMLElement {
    constructor() {
      super();
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      this.textarea = this.querySelector('[data-card-message]');
      this.counter = this.querySelector('[data-card-message-count]');
      this.errorElement = this.querySelector('[data-card-message-error]');
      if (!this.textarea) return;

      this.updateCounter();
      // addEventListener de-duplicates an identical (type, listener) pair, so a
      // re-connect (P1 swaps the cart section markup) cannot double-bind.
      this.textarea.addEventListener('input', this.onInput);
    }

    onInput() {
      this.updateCounter();
      this.textarea.removeAttribute('aria-invalid');
      this.hideError();
    }

    // Raw length — this is what `maxlength` enforces, so the counter matches the cap.
    updateCounter() {
      if (this.counter && this.textarea) this.counter.textContent = this.textarea.value.length;
    }

    get value() {
      return this.textarea ? this.textarea.value.replace(/\s+/g, ' ').trim() : '';
    }

    validate() {
      if (!this.textarea) return null;

      const value = this.value;
      if (!value) {
        this.showError(CardMessageField.EMPTY_MESSAGE_ERROR);
        this.textarea.setAttribute('aria-invalid', 'true');
        this.textarea.focus();
        return null;
      }

      // Write the normalized value back so a native FormData submit carries the
      // trimmed text. Normalizing can only shorten, so `maxlength` still holds.
      if (this.textarea.value !== value) {
        this.textarea.value = value;
        this.updateCounter();
      }
      this.textarea.removeAttribute('aria-invalid');
      this.hideError();
      return value;
    }

    reset() {
      if (this.textarea) {
        this.textarea.value = '';
        this.textarea.removeAttribute('aria-invalid');
      }
      this.updateCounter();
      this.hideError();
    }

    showError(message) {
      if (!this.errorElement) return;
      // Unhide first so role="alert" announces the text change while visible.
      this.errorElement.removeAttribute('hidden');
      this.errorElement.textContent = message;
    }

    hideError() {
      if (!this.errorElement) return;
      this.errorElement.setAttribute('hidden', '');
      this.errorElement.textContent = '';
    }
  }

  CardMessageField.EMPTY_MESSAGE_ERROR = 'Please write your card message before adding it to your cart.';

  customElements.define('card-message-field', CardMessageField);
}
